import { useEffect, useRef, useState } from "react"

import { unpackNotebook, type createReportRequest } from "../utils/reportClient"
import type {
  ReportRevisionEvent,
  ReportRevisionStream,
  ReportSectionProgress,
} from "../utils/reportRevisionTypes"
import type { Notebook, NotebookChatTurn } from "../utils/reportTypes"

interface LiveRequest {
  notebookId: string
  turn: NotebookChatTurn
  targets: Record<string, ReportSectionProgress>
  controller: AbortController
  finished: boolean
}

export function useReportLiveRevisions(options: {
  dirty?: boolean
  recover?: boolean
  notebook: Notebook | null
  streamRevision?: ReportRevisionStream
  request: ReturnType<typeof createReportRequest>
  onDocument: (notebook: Notebook) => void
  onRecoveredDocument?: (notebook: Notebook) => void
  onNotice: (message: string) => void
}) {
  const current = useRef(options)
  current.current = options
  const requests = useRef(new Map<string, LiveRequest>())
  const queues = useRef(new Map<string, Promise<void>>())
  const latestDocuments = useRef(new Map<string, Notebook>())
  const liveStarts = useRef(0)
  const mounted = useRef(true)
  const [version, setVersion] = useState(0)
  const changed = () => {
    if (mounted.current) {
      setVersion((value) => value + 1)
    }
  }
  useEffect(() => {
    mounted.current = true
    const jobs = requests.current
    return () => {
      mounted.current = false
      jobs.forEach((job) => job.controller.abort())
    }
  }, [])

  const notebookId = options.notebook?.id
  const recovering =
    options.recover !== false &&
    options.notebook?.chat_history?.some((turn) => turn.status === "pending")
  useEffect(() => {
    if (!notebookId || !recovering || options.dirty) {
      return
    }
    const controller = new AbortController()
    let generation = 0
    const poll = async () => {
      if ([...requests.current.values()].some((job) => !job.finished)) {
        return
      }
      const tick = ++generation
      // A live request started mid-poll owns newer outcomes, even at the same revision.
      const started = liveStarts.current
      try {
        const loaded = unpackNotebook(
          await current.current.request<Notebook>(
            `/api/notebooks/${encodeURIComponent(notebookId)}`,
            undefined,
            "GET",
            controller.signal,
          ),
        )
        if (
          tick === generation &&
          started === liveStarts.current &&
          !controller.signal.aborted &&
          !current.current.dirty &&
          current.current.recover !== false &&
          current.current.notebook?.id === loaded.id
        ) {
          const install =
            current.current.onRecoveredDocument ?? current.current.onDocument
          install(loaded)
        }
      } catch {
        // A later poll can recover once connectivity returns; saved history stays visible.
      }
    }
    const timer = window.setInterval(() => void poll(), 3000)
    return () => {
      controller.abort()
      window.clearInterval(timer)
    }
  }, [notebookId, recovering, options.dirty])

  const submit = async (
    instructions: string,
    sectionIds: string[],
    persistDraft: (signal: AbortSignal) => Promise<Notebook>,
  ) => {
    const { notebook, streamRevision, request } = current.current
    if (!notebook || !streamRevision) {
      return
    }
    if (
      [...requests.current.values()].filter((job) => !job.finished).length >= 8
    ) {
      current.current.onNotice(
        "Eight requests are already in progress. Send more feedback as one finishes.",
      )
      return
    }
    const id = crypto.randomUUID()
    const job: LiveRequest = {
      notebookId: notebook.id,
      turn: {
        id,
        user_message: instructions,
        assistant_message: "Queued. Each section updates as it finishes.",
        status: "pending",
        created_at: new Date().toISOString(),
        revision_before: notebook.revision,
        section_ids: sectionIds,
      },
      targets: Object.fromEntries(
        sectionIds.map((section) => [
          section,
          { requestId: id, status: "queued" },
        ]),
      ),
      controller: new AbortController(),
      finished: false,
    }
    const previous = queues.current.get(notebook.id) ?? Promise.resolve()
    let release = () => {}
    const completed = new Promise<void>((resolve) => {
      release = resolve
    })
    queues.current.set(notebook.id, completed)
    requests.current.set(id, job)
    liveStarts.current += 1
    changed()
    const update = (event: ReportRevisionEvent) => {
      if (!mounted.current || job.controller.signal.aborted) {
        return
      }
      if (event.request_id !== id) {
        throw new Error("Unexpected revision request")
      }
      if (
        "section_id" in event &&
        !Object.hasOwn(job.targets, event.section_id)
      ) {
        throw new Error("Unexpected revision target")
      }
      if (event.type === "request_started" && event.turn) {
        job.turn = event.turn
      }
      if (event.type === "section_started") {
        job.targets[event.section_id] = { requestId: id, status: "running" }
        job.turn.assistant_message =
          "Updating sections in place. You can send more feedback while I work."
      }
      if (event.type === "section_delta") {
        job.targets[event.section_id] = {
          requestId: id,
          status: "running",
          body: event.body,
        }
      }
      if (event.type === "section_final") {
        job.targets[event.section_id] = {
          requestId: id,
          status: event.changed ? "saved" : "unchanged",
          revision: event.notebook.revision,
        }
        latestDocuments.current.set(event.notebook.id, event.notebook)
        current.current.onDocument(event.notebook)
        const done = Object.values(job.targets).filter((target) =>
          ["saved", "unchanged"].includes(target.status),
        ).length
        job.turn.assistant_message = `${done} of ${sectionIds.length} sections finished. Saved changes are already visible.`
      }
      if (event.type === "section_error") {
        job.targets[event.section_id] = {
          requestId: id,
          status: "failed",
          message: event.error,
        }
      }
      if (event.type === "request_final") {
        job.finished = true
        job.turn = event.turn
        for (const [section, progress] of Object.entries(job.targets)) {
          if (["queued", "running"].includes(progress.status)) {
            job.targets[section] = {
              requestId: id,
              status: "failed",
              message: "This section did not finish. Saved sections are kept.",
            }
          }
        }
        latestDocuments.current.set(event.notebook.id, event.notebook)
        current.current.onDocument(event.notebook)
        current.current.onNotice(event.turn.assistant_message)
      }
      if (event.type === "error") {
        throw new Error(event.error)
      }
      changed()
    }
    try {
      await previous
      job.controller.signal.throwIfAborted()
      const persisted = await persistDraft(job.controller.signal)
      const latest = latestDocuments.current.get(notebook.id)
      const saved =
        latest && latest.revision > persisted.revision ? latest : persisted
      job.turn.revision_before = saved.revision
      await streamRevision(
        saved.id,
        {
          instructions,
          request_id: id,
          expected_revision: saved.revision,
          section_ids: sectionIds,
          stream: true,
        },
        update,
        job.controller.signal,
      )
      if (!job.finished) {
        throw new Error("The stream ended before the request finished.")
      }
    } catch (error) {
      if (!mounted.current) {
        return
      }
      const message =
        error instanceof Error
          ? error.message
          : "The live revision could not finish."
      const savedAny = Object.values(job.targets).some(
        (target) => target.status === "saved",
      )
      job.turn = {
        ...job.turn,
        status: savedAny ? "partial" : "failed",
        assistant_message: `${message} Any sections already saved are kept.`,
      }
      try {
        const recovered = unpackNotebook(
          await request<Notebook | { notebook: Notebook }>(
            `/api/notebooks/${encodeURIComponent(notebook.id)}`,
            undefined,
            "GET",
            job.controller.signal,
          ),
        )
        const outcome = recovered.chat_history?.find((turn) => turn.id === id)
        if (outcome && outcome.status !== "pending") {
          job.turn = outcome
          for (const [section, result] of Object.entries(
            outcome.section_outcomes ?? {},
          )) {
            if (
              Object.hasOwn(job.targets, section) &&
              ["saved", "applied", "unchanged"].includes(result.status)
            ) {
              job.targets[section] = {
                requestId: id,
                status: result.status === "unchanged" ? "unchanged" : "saved",
                revision: result.revision ?? recovered.revision,
              }
            }
          }
        }
        if (mounted.current) {
          current.current.onDocument(recovered)
        }
      } catch {
        // Keep the request and known saved sections visible during a lost connection.
      }
      for (const [section, progress] of Object.entries(job.targets)) {
        if (["queued", "running"].includes(progress.status)) {
          job.targets[section] = { requestId: id, status: "failed", message }
        }
      }
      job.finished = true
      changed()
    } finally {
      // Durable notebook history owns older outcomes; keep a bounded set for
      // recent section statuses and failures that could not be persisted.
      const finished = [...requests.current.entries()].filter(
        ([, request]) => request.finished,
      )
      for (const [requestId] of finished.slice(0, -20)) {
        requests.current.delete(requestId)
      }
      changed()
      release()
      if (queues.current.get(notebook.id) === completed) {
        queues.current.delete(notebook.id)
      }
    }
  }

  // Version tracks imperative stream callbacks; each snapshot is derived from the same jobs.
  void version
  const jobs = [...requests.current.values()].filter(
    (job) => job.notebookId === options.notebook?.id,
  )
  const pending = jobs.filter((job) => !job.finished)
  useEffect(() => {
    if (!options.dirty && !pending.length) {
      return
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [options.dirty, pending.length])
  const progress: Record<string, ReportSectionProgress> = {}
  for (const job of jobs) {
    for (const [id, candidate] of Object.entries(job.targets)) {
      // A completed request already reports its outcome in chat history and
      // the revision notice; only a failure stays on the section afterwards.
      // A newer finished outcome still clears an older failure chip.
      if (job.finished && candidate.status !== "failed") {
        if (progress[id]?.status === "failed") {
          delete progress[id]
        }
        continue
      }
      const previous = progress[id]
      if (
        !previous ||
        !["running", "queued"].includes(previous.status) ||
        (previous.status === "queued" && candidate.status === "running")
      ) {
        progress[id] = candidate
      }
    }
  }
  return {
    submit,
    progress,
    turns: jobs.map((job) => {
      const saved = options.notebook?.chat_history?.find(
        (turn) => turn.id === job.turn.id,
      )
      return saved && saved.status !== "pending" ? saved : job.turn
    }),
    inFlightCount: pending.length,
    activeCount: pending
      .flatMap((job) => Object.values(job.targets))
      .filter((target) => target.status === "running").length,
    queuedCount: pending.filter((job) =>
      Object.values(job.targets).every((target) => target.status === "queued"),
    ).length,
  }
}
